import { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { Play, Stop, Warning } from "@phosphor-icons/react";

// Mode-specific parameter cheatsheet (live read-only values for quick view)
const MODE_PARAMS = {
  market_maker: [
    { key: "order_layers", label: "Layers" },
    { key: "order_size_base_usdt", label: "Size $/layer" },
    { key: "spread_min_pct", label: "Min spread %" },
    { key: "spread_max_pct", label: "Max spread %" },
    { key: "refresh_rate_sec", label: "Refresh s" },
    { key: "inventory_target_pct", label: "Inv target %" },
  ],
  buy_only: [
    { key: "buy_pct_of_ask", label: "% of ask" },
    { key: "buy_order_size_usdt", label: "$/order" },
    { key: "buy_sweep_interval_sec", label: "Interval s" },
    { key: "buy_max_daily_usdt", label: "Daily cap $" },
    { key: "buy_stop_above_price", label: "Stop above $" },
  ],
  sell_only: [
    { key: "sell_pct_of_bid", label: "% of bid" },
    { key: "sell_order_size_token", label: "Token/order" },
    { key: "sell_interval_sec", label: "Interval s" },
    { key: "sell_min_price", label: "Min price $" },
    { key: "sell_max_daily_token", label: "Daily cap tk" },
  ],
};

export default function ControlPanel({ state, onConfigUpdate }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/config");
      setCfg(data);
    } catch (err) {
      console.warn("ControlPanel /config fetch failed:", err?.message || err);
    }
  };
  useEffect(() => {
    loadCfg();
    const id = setInterval(loadCfg, 6000);
    return () => clearInterval(id);
  }, []);

  const running = state?.running;
  const mode = state?.mode || "market_maker";

  const setMode = async (m) => {
    setBusy(true);
    try {
      await api.post("/bot/mode", { mode: m });
      await loadCfg();
      onConfigUpdate?.();
    } finally { setBusy(false); }
  };

  const start = async () => {
    setBusy(true);
    try { await api.post("/bot/start"); } finally { setBusy(false); }
  };
  const stop = async () => {
    if (!window.confirm(t("confirmStop"))) return;
    setBusy(true);
    try { await api.post("/bot/stop"); } finally { setBusy(false); }
  };
  const emergency = async () => {
    if (!window.confirm(t("confirmEmergency"))) return;
    setBusy(true);
    try { await api.post("/bot/emergency-stop"); } finally { setBusy(false); }
  };

  const setQuick = async (overrides) => {
    setBusy(true);
    try {
      await api.put("/config", { overrides });
      await loadCfg();
      onConfigUpdate?.();
    } finally { setBusy(false); }
  };

  const params = MODE_PARAMS[mode] || [];

  return (
    <div className="mq-panel" data-testid="control-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">{t("mode")}</span>
        <span className="mq-label">{mode}</span>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="mode-market-maker" className={`mq-btn ${mode === "market_maker" ? "mq-btn-primary" : ""}`} onClick={() => setMode("market_maker")} disabled={busy}>MM</button>
          <button data-testid="mode-buy-only" className={`mq-btn ${mode === "buy_only" ? "mq-btn-primary" : ""}`} onClick={() => setMode("buy_only")} disabled={busy}>{t("buyOnly")}</button>
          <button data-testid="mode-sell-only" className={`mq-btn ${mode === "sell_only" ? "mq-btn-primary" : ""}`} onClick={() => setMode("sell_only")} disabled={busy}>{t("sellOnly")}</button>
        </div>

        {/* Live mode-specific parameter snapshot */}
        {cfg && (
          <div data-testid={`mode-params-${mode}`} className="border" style={{ borderColor: "var(--mq-border)" }}>
            <div className="px-3 py-2 mq-label flex items-center justify-between" style={{ borderBottom: "1px solid var(--mq-border)" }}>
              <span>active params · {mode}</span>
              <span className="text-[var(--mq-text-3)]">edit in Config →</span>
            </div>
            <div className="grid grid-cols-2 gap-px bg-[var(--mq-border)]">
              {params.map((p) => (
                <div key={p.key} className="bg-[var(--mq-surface)] px-3 py-2" data-testid={`mode-param-${p.key}`}>
                  <div className="mq-label">{p.label}</div>
                  <div className="mq-metric text-sm">{
                    cfg[p.key] === undefined || cfg[p.key] === null || cfg[p.key] === ""
                      ? "—"
                      : (typeof cfg[p.key] === "number" ? cfg[p.key] : String(cfg[p.key]))
                  }</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buy-only quick % selector */}
        {mode === "buy_only" && (
          <div>
            <div className="mq-label mb-2">{t("buyPct")}</div>
            <div className="grid grid-cols-4 gap-2" data-testid="buy-only-percent-selector">
              {[75, 85, 90, 100].map((p) => (
                <button key={p} data-testid={`buy-pct-${p}`}
                  className={`mq-btn ${Number(cfg?.buy_pct_of_ask) === p ? "mq-btn-primary" : ""}`}
                  onClick={() => setQuick({ buy_pct_of_ask: p })} disabled={busy}>{p}%</button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2 border-t" style={{ borderColor: "var(--mq-border)" }}>
          {!running ? (
            <button data-testid="bot-start-button" className="mq-btn mq-btn-success flex items-center justify-center gap-2" onClick={start} disabled={busy}>
              <Play size={14} weight="bold" /> {t("start")}
            </button>
          ) : (
            <button data-testid="bot-stop-button" className="mq-btn flex items-center justify-center gap-2" onClick={stop} disabled={busy}>
              <Stop size={14} weight="bold" /> {t("stop")}
            </button>
          )}
          <button data-testid="emergency-stop-button" className="mq-btn mq-btn-danger flex items-center justify-center gap-2" onClick={emergency} disabled={busy}>
            <Warning size={14} weight="bold" /> {t("emergencyStop")}
          </button>
        </div>
      </div>
    </div>
  );
}
